import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import { JornadaDiaria, JornadaMensal } from '@/service/api'
import { StatusAprovacao } from '@acme/core'
import React from 'react'
import { ExibirDia } from './ExibirDia'
import Ausencias from './ausencias'
import FaltasCell from './faltas'
import OpcoesDigitacao from './opcoes'
import RegistrosCell from './registros-cell'
import { SaldoDiarioCell } from './saldo-diario'

function valueGetterAusencias({
    row: {
        afastamento,
        compensacao,
        compensacaoManual,
        abono,
        descontoDsr,
        folga,
        trocaDiaTrabalho,
        feriado,
        faltaCadastrada,
    },
}: {
    row: JornadaDiaria | JornadaMensal
}) {
    return {
        afastamento:
            !!afastamento &&
            afastamento.statusAprovacao !== StatusAprovacao.REPROVADO
                ? afastamento
                : undefined,
        compensacao: !!compensacao && !feriado ? compensacao : undefined,
        compensacaoManual: compensacaoManual ? compensacaoManual : undefined,
        abono:
            !!abono && abono.statusAprovacao !== StatusAprovacao.REPROVADO
                ? abono
                : undefined,
        descontoDsr,
        folga,
        trocaDiaTrabalho:
            trocaDiaTrabalho && !folga ? trocaDiaTrabalho : undefined,
        faltaCadastrada: faltaCadastrada ? faltaCadastrada : undefined,
    }
}

const MemoziedRegistrosCell = React.memo(
    RegistrosCell,
    (prevProps, nextProps) => {
        const { row: prevRow } = prevProps as {
            row: JornadaDiaria | JornadaMensal
        }
        const { row: nextRow } = nextProps as {
            row: JornadaDiaria | JornadaMensal
        }

        return (
            prevRow.registros.map(r => String(r.updatedAt)).join(',') ===
                nextRow.registros.map(r => String(r.updatedAt)).join(',') &&
            prevRow.dia === nextRow.dia
        )
    },
)

export function mensalColumns(exibirSaldoDiario: boolean) {
    return [
        {
            field: 'dia',
            headerName: 'Dia',
            width: 60,
            renderCell: ExibirDia,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
        },
        {
            field: 'registros',
            headerName: 'Registros',
            minWidth: 680,
            maxWidth: 720,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            headerAlign: 'center',
            renderCell: props => <MemoziedRegistrosCell {...props} />,
        },
        {
            field: 'ausencias',
            headerName: '',
            minWidth: 200,
            flex: 1,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: valueGetterAusencias,
            renderCell: Ausencias,
        },
        {
            field: 'horasTrabalhadas',
            headerName: 'Total',
            width: 60,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => value || '00:00',
        },
        ...(exibirSaldoDiario
            ? [
                  {
                      field: 'saldoDiario',
                      headerName: 'Saldo',
                      width: 60,
                      filterable: false,
                      disableColumnMenu: true,
                      sortable: false,
                      align: 'center',
                      headerAlign: 'center',
                      renderCell: SaldoDiarioCell,
                  },
              ]
            : []),
        {
            field: 'faltas',
            headerName: 'Faltas',
            width: 60,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: FaltasCell,
        },
        {
            field: 'horasExtra',
            headerName: 'H. Extras',
            width: 80,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => value || '00:00',
        },
        {
            field: 'sobreaviso',
            headerName: 'Sobreaviso',
            width: 90,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => value?.total || '00:00',
        },
        {
            ...optionsColumn,
            field: 'opcoes',
            renderCell: OpcoesDigitacao,
        },
    ] as GridColDefPro[]
}

export const diarioColumns: GridColDefPro[] = [
    {
        field: 'nome',
        headerName: 'Funcionário',
        width: 220,
    },
    {
        field: 'registros',
        headerName: 'Registros',
        minWidth: 810,
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
        sortable: false,
        headerAlign: 'center',
        renderCell: RegistrosCell,
    },
    {
        field: 'ausencias',
        headerName: '',
        width: 200,
        filterable: false,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: valueGetterAusencias,
        renderCell: Ausencias,
    },
    {
        field: 'horasTrabalhadas',
        headerName: 'Total',
        width: 60,
        filterable: false,
        disableColumnMenu: true,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: ({ value }) => value || '00:00',
    },
    {
        field: 'faltas',
        headerName: 'Faltas',
        width: 60,
        filterable: false,
        disableColumnMenu: true,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: ({ value }) => value || '00:00',
    },
    {
        field: 'horasExtra',
        headerName: 'H. Extras',
        width: 80,
        filterable: false,
        disableColumnMenu: true,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: ({ value }) => value || '00:00',
    },
    {
        field: 'sobreaviso',
        headerName: 'Sobreaviso',
        width: 90,
        filterable: false,
        disableColumnMenu: true,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: ({ value }) => value?.total || '00:00',
    },
    {
        ...optionsColumn,
        field: 'opcoes',
        renderCell: OpcoesDigitacao,
    },
]
